/**
 * FloatingRecorderWidget
 *
 * A minimal floating widget that shows when recording is active or processing.
 * Allows the engineer to continue using the app while recording/processing.
 */

import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Mic, MicOff, Square, Pause, Play, Minimize2, Maximize2, X, Phone, Loader2, Upload, FileText,
} from 'lucide-react'
import { useCallRecordingContext, formatDuration } from '@/contexts/CallRecordingContext'
import { useRecordingStatus, useRecordingFlow } from '@/hooks/useCallRecordings'
import { blobToBase64, getAudioFormat } from '@/hooks/useCallRecording'
import { cn } from '@/lib/utils'

interface FloatingRecorderWidgetProps {
  onProcessingComplete?: () => void
}

export function FloatingRecorderWidget({ onProcessingComplete }: FloatingRecorderWidgetProps) {
  const {
    isRecording, isPaused, duration, audioLevel, ticketId, ticketSummary, status, recordingId, audioBlob,
    pauseRecording, resumeRecording, stopRecording, cancelRecording, setStatus, setRecordingId, setError,
  } = useCallRecordingContext()

  const [isMinimized, setIsMinimized] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [uploadStarted, setUploadStarted] = useState(false)
  const dragRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null)
  const widgetRef = useRef<HTMLDivElement>(null)

  const { uploadAndProcess } = useRecordingFlow()

  useEffect(() => {
    if (audioBlob && status === 'uploading' && !uploadStarted && !recordingId) {
      setUploadStarted(true)
      handleUpload()
    }
  }, [audioBlob, status, uploadStarted, recordingId])

  useEffect(() => {
    if (status === 'idle') setUploadStarted(false)
  }, [status])

  const handleUpload = async () => {
    if (!audioBlob) return
    try {
      const base64 = await blobToBase64(audioBlob)
      const format = getAudioFormat(audioBlob.type)
      const id = await uploadAndProcess({
        audioData: base64, audioFormat: format, durationSeconds: duration, ticketId: ticketId ?? undefined,
      })
      setRecordingId(id)
      setStatus('processing')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
      setStatus('error')
    }
  }

  const { data: statusData } = useRecordingStatus(
    status === 'processing' && recordingId ? recordingId : null,
    { pollingInterval: 2000 }
  )

  useEffect(() => {
    if (statusData?.status === 'completed') {
      setStatus('complete')
      onProcessingComplete?.()
    } else if (statusData?.status === 'failed') {
      setError(statusData.errorMessage || 'Processing failed')
      setStatus('error')
    }
  }, [statusData, setStatus, setError, onProcessingComplete])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setPosition({ x: window.innerWidth - 280, y: window.innerHeight - 120 })
    }
  }, [])

  useEffect(() => {
    if (!isDragging) return
    const onMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return
      const deltaX = e.clientX - dragRef.current.startX
      const deltaY = e.clientY - dragRef.current.startY
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 280, dragRef.current.initialX + deltaX)),
        y: Math.max(0, Math.min(window.innerHeight - 100, dragRef.current.initialY + deltaY)),
      })
    }
    const onMouseUp = () => { setIsDragging(false); dragRef.current = null }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp) }
  }, [isDragging])

  const shouldShow = isRecording || status === 'recording' || status === 'uploading' || status === 'processing'
  if (!shouldShow) return null

  const isProcessingPhase = status === 'uploading' || status === 'processing'
  const processingProgress = statusData?.progress || (status === 'uploading' ? 20 : 40)
  const processingLabel = status === 'uploading' ? 'Uploading...' : statusData?.status === 'transcribing' ? 'Transcribing...' : statusData?.status === 'generating_notes' ? 'Generating notes...' : 'Processing...'

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return
    setIsDragging(true)
    dragRef.current = { startX: e.clientX, startY: e.clientY, initialX: position.x, initialY: position.y }
  }

  const handleStopRecording = async () => { await stopRecording() }
  const handleCancel = () => { cancelRecording() }

  if (isMinimized) {
    return (
      <div ref={widgetRef} style={{ left: position.x, top: position.y }} className="fixed z-50 cursor-move" onMouseDown={handleMouseDown}>
        <div className={cn('flex items-center gap-2 px-3 py-2 rounded-full shadow-lg transition-all', isProcessingPhase ? 'bg-blue-500' : isPaused ? 'bg-yellow-500' : 'bg-red-500')}>
          {isProcessingPhase ? <Loader2 className="h-3 w-3 text-white animate-spin" /> : <span className={cn('w-3 h-3 rounded-full', isPaused ? 'bg-yellow-200' : 'bg-white animate-pulse')} />}
          <span className="text-white font-mono text-sm font-medium">{isProcessingPhase ? processingLabel : formatDuration(duration)}</span>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-white hover:bg-white/20" onClick={(e) => { e.stopPropagation(); setIsMinimized(false) }}>
            <Maximize2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div ref={widgetRef} style={{ left: position.x, top: position.y }} className={cn('fixed z-50 w-[260px] bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden', isDragging && 'cursor-grabbing')} onMouseDown={handleMouseDown}>
      <div className={cn('flex items-center justify-between px-3 py-2 cursor-move', isProcessingPhase ? 'bg-blue-500' : isPaused ? 'bg-yellow-500' : 'bg-red-500')}>
        <div className="flex items-center gap-2">
          {isProcessingPhase ? <Loader2 className="h-4 w-4 text-white animate-spin" /> : <Phone className="h-4 w-4 text-white" />}
          <span className="text-white text-sm font-medium">{isProcessingPhase ? processingLabel : isPaused ? 'Paused' : 'Recording'}</span>
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6 text-white hover:bg-white/20" onClick={(e) => { e.stopPropagation(); setIsMinimized(true) }} title="Minimize">
          <Minimize2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="p-3 space-y-3">
        {ticketId && <div className="text-xs text-gray-500 truncate">Ticket #{ticketId}: {ticketSummary || 'Untitled'}</div>}
        {isProcessingPhase ? (
          <>
            <div className="flex flex-col items-center gap-3 py-2">
              <div className="flex items-center gap-2">
                {status === 'uploading' && <Upload className="h-5 w-5 text-blue-500" />}
                {status === 'processing' && <FileText className="h-5 w-5 text-blue-500" />}
              </div>
              <p className="text-sm text-gray-600 text-center">
                {status === 'uploading' && 'Uploading audio...'}
                {status === 'processing' && statusData?.status === 'transcribing' && 'Transcribing audio...'}
                {status === 'processing' && statusData?.status === 'generating_notes' && 'Generating AI notes...'}
                {status === 'processing' && !statusData?.status && 'Processing...'}
              </p>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${processingProgress}%` }} />
            </div>
            <p className="text-xs text-center text-gray-500">Recording: {formatDuration(duration)}</p>
            <p className="text-xs text-center text-gray-400 italic">Continue working - we'll notify you when ready</p>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={cn('w-3 h-3 rounded-full', isPaused ? 'bg-yellow-500' : 'bg-red-500 animate-pulse')} />
                <span className="font-mono text-2xl font-semibold text-gray-900">{formatDuration(duration)}</span>
              </div>
              <div className="flex items-center gap-1">
                {isPaused ? <MicOff className="h-4 w-4 text-yellow-500" /> : <Mic className={cn('h-4 w-4 transition-colors', audioLevel > 0.1 ? 'text-green-500' : 'text-gray-400')} />}
                <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className={cn('h-full rounded-full transition-all duration-100', isPaused ? 'bg-yellow-500' : 'bg-green-500')} style={{ width: `${Math.min(100, audioLevel * 100)}%` }} />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); isPaused ? resumeRecording() : pauseRecording() }} className="flex-1">
                {isPaused ? <><Play className="h-4 w-4 mr-1" />Resume</> : <><Pause className="h-4 w-4 mr-1" />Pause</>}
              </Button>
              <Button variant="default" size="sm" onClick={(e) => { e.stopPropagation(); handleStopRecording() }} className="flex-1" style={{ backgroundColor: '#2563eb', color: 'white' }}>
                <Square className="h-4 w-4 mr-1" />Stop
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleCancel() }} className="w-full text-gray-500 hover:text-red-500">
              <X className="h-4 w-4 mr-1" />Cancel Recording
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
